/**
 * Org-partitioned slices — the storage primitive behind "Org A's runs are still
 * Org A's runs after you visit Org B".
 *
 * Two shapes of org-scoped state exist in this app and they want different
 * treatment:
 *
 *  - **Discardable.** Drafts, filters, wizard steps, selections. Switching orgs
 *    should throw it away; `registerOrgScopedReset` covers it in one line.
 *  - **Partitionable.** Live runs, activity indicators, open editor tabs with
 *    unsaved changes. Throwing it away would be data loss dressed up as
 *    isolation — the run keeps executing on the server either way, and the user
 *    who switches back expects to find their work. These keep one slice per
 *    organization, and publish only the active org's slice to the UI.
 *
 * The invariant every partitioned store upholds:
 *
 *     state.byOrg   — every organization's slice. Never rendered.
 *     state.<field> — a flattened copy of the ACTIVE slice. Only this is rendered.
 *
 * Writes always address a slice by the org that owns it, so a late SSE frame
 * from Org A lands in Org A's slice by construction rather than by remembering
 * to check. It becomes visible again if and when the user returns to Org A.
 */

import type { OrgId } from './types';

/**
 * Key used for state created while no organization is selected (signed out, or
 * before the first user fetch resolves). Kept distinct from any real org id so
 * such state can never be published under a real one.
 */
const NO_ORG_KEY = '__no_org__';

function sliceKey(orgId: OrgId | null): string {
  return orgId ?? NO_ORG_KEY;
}

/**
 * Read one organization's slice, or a fresh empty slice if it has none.
 *
 * Does not mutate or memoise: an org with no state does not need an entry, and
 * creating one on read would make `byOrg` grow on every render.
 */
export function readOrgSlice<S>(
  byOrg: ReadonlyMap<string, S>,
  orgId: OrgId | null,
  createEmpty: () => S,
): S {
  return byOrg.get(sliceKey(orgId)) ?? createEmpty();
}

/** Return a new partition map with `orgId`'s slice replaced. */
export function writeOrgSlice<S>(
  byOrg: ReadonlyMap<string, S>,
  orgId: OrgId | null,
  slice: S,
): Map<string, S> {
  const next = new Map(byOrg);
  next.set(sliceKey(orgId), slice);
  return next;
}

/** Return a new partition map with `orgId`'s slice removed. */
export function dropOrgSlice<S>(
  byOrg: ReadonlyMap<string, S>,
  orgId: OrgId | null,
): Map<string, S> {
  const next = new Map(byOrg);
  next.delete(sliceKey(orgId));
  return next;
}
